import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Mail, MessageCircle, Globe, Sparkles, Shield, Heart, Send, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/hooks/useAuth";
import api from "@/lib/api";
import { toast } from "sonner";

// =================== /about ===================
export function AboutPage() {
  const { user, logout } = useAuth({ redirectIfUnauthed: false });

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB]">
      <Helmet>
        <title>About AgentReply — Our story, mission &amp; team</title>
        <meta name="description" content="AgentReply is built by a small team obsessed with making AI agents accessible to every business. Read about our mission, principles and the people behind the product." />
        <link rel="canonical" href="https://agentreply.io/about" />
        <meta property="og:title" content="About AgentReply — Our story, mission &amp; team" />
        <meta property="og:description" content="AgentReply is built by a small team obsessed with making AI agents accessible to every business." />
        <meta property="og:url" content="https://agentreply.io/about" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About AgentReply",
          "url": "https://agentreply.io/about",
          "mainEntity": {
            "@type": "Organization",
            "name": "AgentReply",
            "url": "https://agentreply.io",
            "logo": "https://agentreply.io/logo512.png",
            "foundingDate": "2026",
            "description": "AgentReply is a SaaS platform that lets anyone build customisable AI agents that reply on WhatsApp, Instagram, Messenger and websites.",
            "knowsAbout": ["Artificial Intelligence", "WhatsApp Business API", "Customer Support Automation", "Conversational AI", "Meta Graph API"],
            "areaServed": "Worldwide",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer support",
              "email": "support@agentreply.io",
              "availableLanguage": ["English"]
            }
          }
        })}</script>
      </Helmet>

      <Navbar user={user} onLogout={logout} />

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="about-page">
        <header className="mb-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-4 flex items-center gap-2">
            <Sparkles className="w-3 h-3" strokeWidth={2} />About AgentReply
          </div>
          <h1 className="font-serif text-5xl md:text-6xl mb-8 leading-[1.05]">
            We build AI agents that <em className="italic text-[#D95B38]">actually</em> reply.
          </h1>
          <p className="font-mono text-base md:text-lg text-[#A39A8F] leading-relaxed max-w-3xl">
            AgentReply was born from a simple observation: businesses lose customers every day because they can't reply fast enough.
            We're building the easiest way to launch a custom AI agent that handles WhatsApp, Instagram, Messenger and the web — without engineers,
            without complicated flowcharts, without month-long onboarding.
          </p>
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-16">
          <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8">
            <Heart className="w-6 h-6 text-[#D95B38] mb-4" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl mb-3">Our mission</h2>
            <p className="font-mono text-sm text-[#A39A8F] leading-relaxed">
              Give every business — from the corner café to the global brand — an AI agent that sounds like them, knows their product,
              and replies 24/7. No code, no compromise.
            </p>
          </div>
          <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8">
            <Shield className="w-6 h-6 text-[#D95B38] mb-4" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl mb-3">Privacy by default</h2>
            <p className="font-mono text-sm text-[#A39A8F] leading-relaxed">
              Your data, your customers' messages, your knowledge base — they stay yours. Encrypted at rest, never sold,
              never used to train third-party models. Full disclosure in our <Link to="/legal/privacy" className="text-[#D95B38] underline">privacy policy</Link>.
            </p>
          </div>
        </section>

        <section className="mb-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-4">What we believe</div>
          <h2 className="font-serif text-3xl md:text-4xl mb-8">Five principles guiding everything we ship.</h2>
          <div className="space-y-6">
            {[
              {n: "01", t: "Sound human, not robotic.", d: "AI replies should feel like a friendly teammate, not a decision-tree bot. We pair Claude Sonnet 4.5 with per-contact CRM memory so every reply is personal."},
              {n: "02", t: "Setup in minutes, not weeks.", d: "Onboarding takes 5 minutes. Pick a persona, paste your knowledge, share the link. If it took longer we'd see it as a bug."},
              {n: "03", t: "Channel-native, not iframe-only.", d: "Your agent should reply on the actual WhatsApp number your customers already use — not just a website popup."},
              {n: "04", t: "Honest pricing.", d: "$9.99/month flat, $70/year, or $250 lifetime. No per-message fees, no enterprise gatekeeping, no surprise overages."},
              {n: "05", t: "Made for founders.", d: "We're a small team. We answer support emails ourselves. We ship every week. The roadmap is built on real customer feedback, not focus groups."},
            ].map((p) => (
              <div key={p.n} className="flex gap-6 items-start">
                <div className="font-mono text-xs text-[#D95B38] pt-1 min-w-[2rem]">{p.n}</div>
                <div>
                  <h3 className="font-serif text-xl mb-2">{p.t}</h3>
                  <p className="font-mono text-sm text-[#A39A8F] leading-relaxed">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-12 mb-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              {n: "3", l: "Channels supported"},
              {n: "9", l: "Voice options"},
              {n: "30+", l: "Languages"},
              {n: "24/7", l: "Uptime"},
            ].map((s) => (
              <div key={s.l}>
                <div className="font-serif text-4xl md:text-5xl text-[#D95B38] mb-2">{s.n}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">{s.l}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="font-serif text-3xl mb-4">Get in touch.</h2>
          <p className="font-mono text-sm text-[#A39A8F] leading-relaxed max-w-2xl mb-6">
            Questions, feedback, press, partnership ideas — we read every email. The fastest way to reach us is the contact page.
          </p>
          <div className="flex gap-3 flex-wrap">
            <Link to="/contact" className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-5 py-3 btn-press inline-flex items-center gap-2" data-testid="about-contact-cta">
              <Mail className="w-4 h-4" strokeWidth={1.5} /> Contact us
            </Link>
            <Link to="/help" className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] px-5 py-3 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors inline-flex items-center gap-2">
              <Globe className="w-4 h-4" strokeWidth={1.5} /> Browse the help center
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

// =================== /contact ===================
export function ContactPage() {
  const { user, logout } = useAuth({ redirectIfUnauthed: false });
  const [form, setForm] = useState({ name: "", email: "", business_name: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email and message are all required."); return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", { ...form, service_type: "other" });
      setSent(true);
      toast.success("Got it — we'll be in touch within 24 hours.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Please try again or email support@agentreply.io directly.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB]">
      <Helmet>
        <title>Contact AgentReply — We're here to help</title>
        <meta name="description" content="Get in touch with the AgentReply team. Email support, sales questions, press inquiries and partnership requests — we read every message and reply within 24 hours." />
        <link rel="canonical" href="https://agentreply.io/contact" />
        <meta property="og:title" content="Contact AgentReply — We're here to help" />
        <meta property="og:description" content="Email support, sales, press and partnership inquiries. We reply within 24 hours." />
        <meta property="og:url" content="https://agentreply.io/contact" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact AgentReply",
          "url": "https://agentreply.io/contact",
          "mainEntity": {
            "@type": "Organization",
            "name": "AgentReply",
            "url": "https://agentreply.io",
            "contactPoint": [
              {"@type": "ContactPoint", "contactType": "customer support", "email": "support@agentreply.io", "availableLanguage": ["English"], "areaServed": "Worldwide"},
              {"@type": "ContactPoint", "contactType": "sales", "email": "support@agentreply.io", "availableLanguage": ["English"]},
              {"@type": "ContactPoint", "contactType": "press", "email": "support@agentreply.io", "availableLanguage": ["English"]}
            ]
          }
        })}</script>
      </Helmet>

      <Navbar user={user} onLogout={logout} />

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="contact-page">
        <header className="mb-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-4 flex items-center gap-2">
            <MessageCircle className="w-3 h-3" strokeWidth={2} />Contact us
          </div>
          <h1 className="font-serif text-5xl md:text-6xl mb-6 leading-[1.05]">
            Talk to a human.
          </h1>
          <p className="font-mono text-base text-[#A39A8F] leading-relaxed max-w-2xl">
            Questions about features, billing, partnerships or press? Drop us a line below. We read every email and reply within 24 hours
            (usually faster).
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10">
          {/* Form */}
          <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10">
            {sent ? (
              <div className="text-center py-8" data-testid="contact-success">
                <div className="font-serif text-3xl text-[#D95B38] mb-3">Thanks — we've got it.</div>
                <p className="font-mono text-sm text-[#A39A8F] mb-6">
                  We'll reply within 24 hours to <span className="text-[#F5F2EB]">{form.email}</span>.
                </p>
                <button
                  onClick={() => { setSent(false); setForm({ name: "", email: "", business_name: "", message: "" }); }}
                  className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 px-5 py-2.5 hover:bg-[#F5F2EB] hover:text-[#0F0D0C] transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-2 block">Your name *</label>
                    <input
                      required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="contact-input-name"
                      className="w-full bg-[#0F0D0C] border border-[#F5F2EB]/15 px-4 py-3 font-mono text-sm text-[#F5F2EB] focus:outline-none focus:border-[#D95B38] transition-colors"
                      placeholder="Jane Smith"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-2 block">Email *</label>
                    <input
                      required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="contact-input-email"
                      className="w-full bg-[#0F0D0C] border border-[#F5F2EB]/15 px-4 py-3 font-mono text-sm text-[#F5F2EB] focus:outline-none focus:border-[#D95B38] transition-colors"
                      placeholder="jane@yourcompany.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-2 block">Company (optional)</label>
                  <input
                    value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                    data-testid="contact-input-company"
                    className="w-full bg-[#0F0D0C] border border-[#F5F2EB]/15 px-4 py-3 font-mono text-sm text-[#F5F2EB] focus:outline-none focus:border-[#D95B38] transition-colors"
                    placeholder="Acme Inc."
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-2 block">How can we help? *</label>
                  <textarea
                    required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="contact-input-message"
                    className="w-full bg-[#0F0D0C] border border-[#F5F2EB]/15 px-4 py-3 font-mono text-sm text-[#F5F2EB] focus:outline-none focus:border-[#D95B38] transition-colors resize-y"
                    placeholder="Tell us about your business, what you're trying to build, or anything you'd like us to know."
                  />
                </div>
                <button
                  type="submit" disabled={submitting} data-testid="contact-submit"
                  className="font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-6 py-3 btn-press flex items-center gap-2 disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
                  {submitting ? "Sending…" : "Send message"}
                </button>
                <p className="font-mono text-[10px] text-[#7A7068] leading-relaxed">
                  By submitting, you agree to our <Link to="/legal/privacy" className="text-[#D95B38] underline">privacy policy</Link>.
                  We never sell or share your details.
                </p>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-6">
              <Mail className="w-5 h-5 text-[#D95B38] mb-3" strokeWidth={1.5} />
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-2">Email us directly</div>
              <a href="mailto:support@agentreply.io" className="font-mono text-sm text-[#F5F2EB] hover:text-[#D95B38] transition-colors" data-testid="contact-direct-email">
                support@agentreply.io
              </a>
              <p className="font-mono text-xs text-[#A39A8F] leading-relaxed mt-3">
                Replies within 24 hours, Monday to Friday.
              </p>
            </div>

            <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-6">
              <Globe className="w-5 h-5 text-[#D95B38] mb-3" strokeWidth={1.5} />
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-3">Looking for answers?</div>
              <p className="font-mono text-xs text-[#A39A8F] leading-relaxed mb-3">
                The help center covers most common setup questions.
              </p>
              <Link to="/help" className="font-mono text-xs uppercase tracking-[0.25em] text-[#D95B38] hover:underline" data-testid="contact-help-link">
                Browse help center →
              </Link>
            </div>

            <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-3">For agencies</div>
              <p className="font-mono text-xs text-[#A39A8F] leading-relaxed mb-3">
                Building agents for clients? Check out our reseller programme.
              </p>
              <Link to="/services" className="font-mono text-xs uppercase tracking-[0.25em] text-[#D95B38] hover:underline">
                Partner with us →
              </Link>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}
